// src/pages/Dashboard.jsx
import React from 'react';
import { 
  Users, 
  Target, 
  UserCheck, 
  DollarSign,
  TrendingUp,
  BarChart3,
  Activity,
  MessageSquare
} from 'lucide-react';

const Dashboard = () => {
  const stats = [
    {
      name: 'Total Users',
      value: '12,345',
      change: '+12%',
      changeType: 'increase',
      icon: Users,
      color: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Active Advertisers',
      value: '1,234',
      change: '+8%',
      changeType: 'increase',
      icon: Target,
      color: 'from-green-600 to-green-700'
    },
    {
      name: 'Active Affiliates',
      value: '5,678',
      change: '+15%',
      changeType: 'increase',
      icon: UserCheck,
      color: 'from-purple-600 to-purple-700'
    },
    {
      name: 'Monthly Revenue',
      value: '$2.4M',
      change: '+23%',
      changeType: 'increase',
      icon: DollarSign,
      color: 'from-yellow-600 to-yellow-700'
    }
  ];

  const recentActivity = [
    { id: 1, type: 'user', message: 'New advertiser registration: TechCorp Inc.', time: '2 minutes ago' },
    { id: 2, type: 'payment', message: 'Weekly affiliate payout processed: $125,000', time: '15 minutes ago' },
    { id: 3, type: 'campaign', message: 'High-performing campaign reached 1M impressions', time: '1 hour ago' },
    { id: 4, type: 'support', message: 'New support ticket from affiliate partner', time: '2 hours ago' },
    { id: 5, type: 'system', message: 'System maintenance completed successfully', time: '4 hours ago' }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-slate-100">Dashboard Overview</h2>
        <p className="text-slate-400 mt-2">Monitor your network performance and key metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <div key={stat.name} className="admin-glass rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium">{stat.name}</p>
                  <p className="text-3xl font-bold text-slate-100 mt-2">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
                    <span className="text-green-400 text-sm font-medium">{stat.change}</span>
                    <span className="text-slate-400 text-sm ml-1">from last month</span>
                  </div>
                </div>
                <div className={`p-3 bg-gradient-to-r ${stat.color} rounded-lg`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart Placeholder */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-100">Network Performance</h3>
            <BarChart3 className="w-5 h-5 text-slate-400" />
          </div>
          
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Performance chart will be implemented</p>
              <p className="text-slate-500 text-sm">with real-time analytics data</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-100">Recent Activity</h3>
            <Activity className="w-5 h-5 text-slate-400" />
          </div>
          
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-3 bg-slate-800/50 rounded-lg">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-300 text-sm">{activity.message}</p>
                  <p className="text-slate-500 text-xs mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-slate-700">
            <button className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors">
              View all activity →
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
        <h3 className="text-xl font-bold text-slate-100 mb-6">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors text-left">
            <Users className="w-6 h-6 text-blue-400 mb-2" />
            <h4 className="text-slate-100 font-medium">Manage Users</h4>
            <p className="text-slate-400 text-sm">View and manage user accounts</p>
          </button>
          
          <button className="p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors text-left">
            <MessageSquare className="w-6 h-6 text-green-400 mb-2" />
            <h4 className="text-slate-100 font-medium">Review Contacts</h4>
            <p className="text-slate-400 text-sm">Check new contact submissions</p>
          </button>
          
          <button className="p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors text-left">
            <BarChart3 className="w-6 h-6 text-purple-400 mb-2" />
            <h4 className="text-slate-100 font-medium">Generate Reports</h4>
            <p className="text-slate-400 text-sm">Create performance reports</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

